/**
 * Admin → Repair Knowledge Base → Ad Settings
 * Route: /admin/repair-knowledge-base/ads
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import {
  fetchAdSenseConfig,
  saveAdSenseConfig,
  invalidateAdSenseCache,
  DEFAULT_ADSENSE_CONFIG,
  type AdSenseConfig,
} from '../../lib/adsense-config';
import { ArrowLeft, Save, Loader, Check, AlertTriangle } from '../../lib/icons';

const TOKEN_KEY = 'gfs_admin_token';

function getToken(): string {
  return sessionStorage.getItem(TOKEN_KEY) ?? '';
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function Toggle({
  label, checked, onChange,
}: {
  label:    string;
  checked:  boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <span className="text-sm text-[#E0E0E0]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
          checked ? 'bg-[#39FF14]' : 'bg-gray-700'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

// ── Slot row ──────────────────────────────────────────────────────────────────

function SlotField({
  label, value, onChange, placeholder,
}: {
  label:       string;
  value:       string;
  onChange:    (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 font-mono"
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminAdSettingsPage() {
  const [config,  setConfig]  = useState<AdSenseConfig>(DEFAULT_ADSENSE_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    invalidateAdSenseCache();
    fetchAdSenseConfig()
      .then(c => { setConfig(c); setLoading(false); })
      .catch(() => { setConfig(DEFAULT_ADSENSE_CONFIG); setLoading(false); });
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await saveAdSenseConfig(config, getToken());
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function setPub(v: string) {
    setConfig(c => ({ ...c, publisher_id: v }));
  }
  function setEnabled(v: boolean) {
    setConfig(c => ({ ...c, enabled: v }));
  }
  function setPlacement<K extends keyof AdSenseConfig['placements']>(
    key: K, value: AdSenseConfig['placements'][K],
  ) {
    setConfig(c => ({ ...c, placements: { ...c.placements, [key]: value } }));
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead title="Ad Settings | GameFix Admin" noIndex />

      <div className="max-w-2xl mx-auto px-6 py-10">

        {/* Back link */}
        <Link
          to="/admin/repair-knowledge-base"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E0E0E0] mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Repair Knowledge Base
        </Link>

        <h1 className="text-2xl font-black text-white mb-1">Ad Settings</h1>
        <p className="text-sm text-gray-500 mb-8">
          Manage Google AdSense placements in the public Repair Database.
          Ads will only appear on <code className="text-[#00BFFF]">/repair-database/*</code> pages.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 text-gray-500 py-12">
            <Loader className="h-5 w-5 animate-spin" />
            <span>Loading…</span>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-8">

            {/* Global toggle */}
            <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-bold text-white">Global</h2>
              <Toggle
                label="Enable ads on Repair Database"
                checked={config.enabled}
                onChange={setEnabled}
              />
              <SlotField
                label="AdSense Publisher ID"
                value={config.publisher_id}
                onChange={setPub}
                placeholder="ca-pub-XXXXXXXXXXXXXXXX"
              />
              <p className="text-xs text-gray-600">
                Find your Publisher ID in AdSense → Account → Account information.
              </p>
            </section>

            {/* Banner placement */}
            <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-bold text-white">Top Banner</h2>
              <p className="text-xs text-gray-500">
                Appears below the device title, before the Repair Guides table.
              </p>
              <Toggle
                label="Enable top banner"
                checked={config.placements.banner_enabled}
                onChange={v => setPlacement('banner_enabled', v)}
              />
              <SlotField
                label="Banner Ad Slot ID"
                value={config.placements.banner_slot_id}
                onChange={v => setPlacement('banner_slot_id', v)}
                placeholder="1234567890"
              />
            </section>

            {/* In-content placement */}
            <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-bold text-white">In-Content</h2>
              <p className="text-xs text-gray-500">
                Appears between the Repair Guides table and Known-Good Measurements.
              </p>
              <Toggle
                label="Enable in-content ad"
                checked={config.placements.in_content_enabled}
                onChange={v => setPlacement('in_content_enabled', v)}
              />
              <SlotField
                label="In-Content Ad Slot ID"
                value={config.placements.in_content_slot_id}
                onChange={v => setPlacement('in_content_slot_id', v)}
                placeholder="0987654321"
              />
            </section>

            {/* Sidebar placement */}
            <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-bold text-white">Sidebar <span className="text-xs font-normal text-gray-600">(disabled by default)</span></h2>
              <p className="text-xs text-gray-500">
                Desktop only — sits in the right-hand sidebar column.
              </p>
              <Toggle
                label="Enable sidebar ad"
                checked={config.placements.sidebar_enabled}
                onChange={v => setPlacement('sidebar_enabled', v)}
              />
              <SlotField
                label="Sidebar Ad Slot ID"
                value={config.placements.sidebar_slot_id}
                onChange={v => setPlacement('sidebar_slot_id', v)}
                placeholder="1122334455"
              />
            </section>

            {/* ads.txt reminder */}
            <div className="bg-yellow-900/20 border border-yellow-800/40 rounded-xl p-4 text-sm text-yellow-200/80">
              <p className="font-semibold mb-1">ads.txt required</p>
              <p className="text-xs text-yellow-200/60 leading-relaxed">
                Add your Publisher ID to <code>/public/ads.txt</code> in the project before going live.
                Replace <code>XXXXXXXXXXXXXXXX</code> with your actual Publisher ID.
              </p>
              <pre className="mt-2 text-[11px] bg-black/30 rounded p-2 overflow-x-auto">
                {'google.com, ca-pub-XXXXXXXXXXXXXXXX, DIRECT, f08c47fec0942fa0'}
              </pre>
            </div>

            {/* Save button */}
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 bg-[#FF1744] hover:bg-[#D50000] disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm"
              >
                {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
              {saved && (
                <span className="flex items-center gap-1.5 text-sm text-green-400">
                  <Check className="h-4 w-4" />
                  Saved
                </span>
              )}
              {error && (
                <span className="flex items-center gap-1.5 text-sm text-red-400">
                  <AlertTriangle className="h-4 w-4" />
                  {error}
                </span>
              )}
            </div>

          </form>
        )}
      </div>
    </div>
  );
}
